import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const TAG_STYLES = {
  'New Feature': 'bg-[#171717] text-white',
  'Update':      'bg-[#fafafa] text-[#4d4d4d]',
  'Fix':         'bg-[#fafafa] text-[#4d4d4d]',
  'Announcement':'bg-[#fafafa] text-[#4d4d4d]',
};

export default function News() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tag, setTag] = useState('New Feature');
  const [publishing, setPublishing] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchNews = async () => {
    try {
      const res = await api.get('/api/news/');
      setItems(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    document.title = "What's New — Protovera";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (el) el.setAttribute('content', content);
    };
    const setCanonical = (href) => {
      let el = document.querySelector('link[rel="canonical"]');
      if (el) el.setAttribute('href', href);
    };
    setMeta('description', 'Product updates, new features, and releases from Protovera — the AI research protocol generator for life sciences researchers.');
    setCanonical(`${window.location.origin}/news`);
    return () => {
      document.title = 'Protovera — AI Research Protocol Generator for Life Sciences';
      setCanonical(`${window.location.origin}/`);
    };
  }, []);

  useEffect(() => { fetchNews(); }, []);

  const handlePublish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    setFormError('');
    try {
      await api.post('/api/news/', { title, content, tag });
      setTitle(''); setContent(''); setTag('New Feature');
      setShowForm(false);
      fetchNews();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to publish.');
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await api.delete(`/api/news/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <header className="bg-white" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px -1px 0px 0px inset' }}>
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-base font-semibold text-[#171717]" style={{ letterSpacing: '-0.32px' }}>
            Protovera
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to="/" className="text-sm text-[#4d4d4d] font-medium hover:text-[#171717]">← Back to App</Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-[#4d4d4d] hover:text-[#171717]">Sign In</Link>
                <Link
                  to="/register"
                  className="text-sm bg-[#171717] text-white px-4 py-1.5 rounded-md font-medium hover:bg-[#2e2e2e] transition-colors"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-semibold text-[#171717]" style={{ letterSpacing: '-2.4px' }}>What's New</h1>
            <p className="text-[#808080] mt-1 text-sm">Product updates, fixes, and releases from Protovera.</p>
          </div>
          {user?.plan === 'enterprise' && (
            <button
              onClick={() => setShowForm(!showForm)}
              className="px-4 py-2 bg-[#171717] text-white rounded-md text-sm font-medium hover:bg-[#2e2e2e] transition-colors"
            >
              {showForm ? 'Cancel' : '+ New Post'}
            </button>
          )}
        </div>

        {/* Admin publish form */}
        {showForm && user?.plan === 'enterprise' && (
          <form
            onSubmit={handlePublish}
            className="bg-white rounded-xl p-6 mb-8 space-y-4"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          >
            <h2 className="font-semibold text-[#171717] text-sm" style={{ letterSpacing: '-0.32px' }}>Publish New Update</h2>

            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-[#4d4d4d] mb-1">Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  placeholder="e.g. Clinical Trial Finder is now live"
                  className="w-full rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
                  style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#4d4d4d] mb-1">Tag</label>
                <select
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  className="rounded-md px-3 py-2 text-sm text-[#171717] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
                  style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
                >
                  <option>New Feature</option>
                  <option>Update</option>
                  <option>Fix</option>
                  <option>Announcement</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-[#4d4d4d] mb-1">Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                required
                rows={5}
                placeholder="Describe the update in detail..."
                className="w-full rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef] resize-none"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              />
            </div>

            {formError && <p className="text-sm text-red-600">{formError}</p>}

            <button
              type="submit"
              disabled={publishing}
              className="px-6 py-2 bg-[#171717] text-white rounded-md text-sm font-medium hover:bg-[#2e2e2e] disabled:opacity-50 transition-colors"
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          </form>
        )}

        {/* News feed */}
        {loading ? (
          <div className="text-center py-16 text-[#808080]">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#808080]">
            <p className="text-4xl mb-3">📰</p>
            <p className="text-sm">No updates yet. Check back soon.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-xl p-6"
                style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span
                        className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${TAG_STYLES[item.tag] || 'bg-[#fafafa] text-[#4d4d4d]'}`}
                        style={{ boxShadow: 'rgba(0,0,0,0.06) 0px 0px 0px 1px' }}
                      >
                        {item.tag}
                      </span>
                      <span className="text-xs text-[#808080]">{item.created_at}</span>
                    </div>
                    <h2 className="text-base font-semibold text-[#171717]" style={{ letterSpacing: '-0.32px' }}>{item.title}</h2>
                    <p className="mt-2 text-[#4d4d4d] text-sm leading-relaxed whitespace-pre-line">{item.content}</p>
                  </div>
                  {user?.plan === 'enterprise' && (
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-[#ebebeb] hover:text-red-500 text-lg flex-shrink-0 transition-colors"
                      title="Delete post"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
